/**
 * Типизированный wrapper над localStorage.
 *
 * Зачем: раньше `localStorage.getItem / setItem / JSON.parse / try-catch`
 * был раскидан по десяти файлам, каждый со своим обработчиком ошибок и
 * своим подходом к дефолтам. Теперь одна общая реализация:
 *
 *   storage.get<T>(key, fallback) — читает и парсит JSON, отдаёт fallback
 *                                   при отсутствии/ошибке/SSR.
 *   storage.set<T>(key, value)    — сериализует и пишет, тихо игнорирует
 *                                   quota exceeded и private mode.
 *   storage.remove(key)           — удаляет.
 *   storage.getCached<T>(key, ms) — читает { data, ts } и проверяет TTL.
 *   storage.setCached<T>(key, v)  — пишет { data, ts: Date.now() }.
 *
 * Все операции SSR-safe (проверяют наличие window), поэтому модуль
 * можно безопасно импортировать в файлы, которые рендерятся и на сервере
 * тоже — на сервере get всегда вернёт fallback.
 */

function isAvailable(): boolean {
  return typeof window !== "undefined" && typeof window.localStorage !== "undefined";
}

export function get<T>(key: string, fallback: T): T {
  if (!isAvailable()) return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    if (raw === null) return fallback;
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
}

export function set<T>(key: string, value: T): void {
  if (!isAvailable()) return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* quota exceeded / private mode — тихий провал, не хочется ронять UI */
  }
}

export function remove(key: string): void {
  if (!isAvailable()) return;
  try {
    window.localStorage.removeItem(key);
  } catch { /* noop */ }
}

/**
 * Читает кэш-запись формата { data, ts } и проверяет TTL.
 * Возвращает `null` если записи нет, она устарела или повреждена.
 */
export function getCached<T>(key: string, maxAgeMs: number): T | null {
  const cached = get<{ data: T; ts: number } | null>(key, null);
  if (!cached || typeof cached !== "object") return null;
  if (typeof cached.ts !== "number") return null;
  if (Date.now() - cached.ts > maxAgeMs) return null;
  return cached.data;
}

/** Пишет кэш-запись с текущим timestamp. */
export function setCached<T>(key: string, data: T): void {
  set(key, { data, ts: Date.now() });
}

/** Возвращает timestamp кэш-записи или null. Полезно для UI «обновлено X минут назад». */
export function getCachedAt(key: string): number | null {
  const cached = get<{ ts: number } | null>(key, null);
  if (!cached || typeof cached !== "object" || typeof cached.ts !== "number") return null;
  return cached.ts;
}
